import { z } from "zod/v4";

import {
  ExperienceProductTargetingDtoDescriptions,
  ProductTargetingExpressionNodeQueryDescriptions,
} from "../descriptions/experience-product-targeting-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import { ExpressionNodeOperator } from "./audiences";

export const ProductAttributeList = [
  "productId",
  "collection",
  "tag",
  "price",
  "inventory",
] as const;

export type ProductAttribute = (typeof ProductAttributeList)[number];

export const ProductFilterList = [
  "includes",
  "doesNotInclude",
  "greaterThan",
  "lessThan",
] as const;

export type ProductFilter = (typeof ProductFilterList)[number];

export const ProductTargetingTypeList = ["productPage", "productCard"] as const;
export type ProductTargetingType = (typeof ProductTargetingTypeList)[number];

export const ProductTargetingExpressionNodeQuery = z.object({
  key: z
    .enum(ProductAttributeList)
    .describe(ProductTargetingExpressionNodeQueryDescriptions.key),
  value: z
    .string()
    .or(z.number())
    .describe(ProductTargetingExpressionNodeQueryDescriptions.value),
  filter: z
    .enum(ProductFilterList)
    .describe(ProductTargetingExpressionNodeQueryDescriptions.filter),
  type: z
    .enum(ProductAttributeList)
    .describe(ProductTargetingExpressionNodeQueryDescriptions.type),
});

export type ProductTargetingExpressionNodeQueryType = z.infer<
  typeof ProductTargetingExpressionNodeQuery
>;

export const ProductTargetingExpressionNode = z
  .object({
    query: ProductTargetingExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  );

export type ProductTargetingExpressionNodeType = z.infer<
  typeof ProductTargetingExpressionNode
>;

export const ExperienceProductTargetingDtoSchema = z.object({
  id: z.uuid().describe(ExperienceProductTargetingDtoDescriptions.id),
  experienceId: z
    .uuid()
    .describe(ExperienceProductTargetingDtoDescriptions.experienceId),
  target: z
    .enum(ProductTargetingTypeList)
    .describe(ExperienceProductTargetingDtoDescriptions.target),
  expression: z
    .array(ProductTargetingExpressionNode)
    .nullish()
    .describe(ExperienceProductTargetingDtoDescriptions.expression),
});

export type ExperienceProductTargetingDto = z.infer<
  typeof ExperienceProductTargetingDtoSchema
>;

export const CreateExperienceProductTargetingDtoSchema =
  ExperienceProductTargetingDtoSchema.extend({
    experienceId: z.uuid().optional(),
  });

export type CreateExperienceProductTargetingDto = z.infer<
  typeof CreateExperienceProductTargetingDtoSchema
>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<
  typeof ProductTargetingExpressionNodeQueryDescriptions,
  ProductTargetingExpressionNodeQueryType
>();
assertDescriptions<
  typeof ExperienceProductTargetingDtoDescriptions,
  ExperienceProductTargetingDto
>();
